// Shared content for CareSenta: modules, pricing, integrations, case studies, FAQs.

export const MODULES = [
  {
    slug: "prior-auth",
    index: "M-01",
    name: "Prior Auth Autopilot",
    tagline: "Submit, track, and close prior authorizations in minutes, not days.",
    description:
      "Reads the order, pulls clinical context from your EHR, writes the payer-specific narrative, and files electronically. Escalates only the edge cases a physician must touch.",
    stats: [
      { label: "AVG APPROVAL TIME", value: "4.1h", delta: "-82%" },
      { label: "APPROVAL RATE", value: "94%", delta: "+31%" },
      { label: "HOURS / MO / PROVIDER", value: "28h", delta: "reclaimed" },
    ],
    triggers: "pa.required",
    roles: ["Admin", "Billing", "MA"],
  },
  {
    slug: "scribe",
    index: "M-02",
    name: "Clinical Note Scribe",
    tagline: "Ambient SOAP notes drafted before the patient leaves the exam room.",
    description:
      "Whisper-powered transcription + Claude-drafted Subjective/Objective/Assessment/Plan in your voice. Suggests ICD-10 and CPT codes. Physician reviews and signs.",
    stats: [
      { label: "NOTES DRAFTED / DAY", value: "38", delta: "per provider" },
      { label: "CODE SUGGESTION ACCURACY", value: "97%", delta: "+14%" },
      { label: "DOC TIME", value: "-3.2h/day", delta: "per provider" },
    ],
    triggers: "encounter.completed",
    roles: ["Physician", "Admin"],
  },
  {
    slug: "no-show",
    index: "M-03",
    name: "No-Show Predictor",
    tagline: "Predict, remind, and fill canceled slots automatically.",
    description:
      "Risk-scores every appointment 72 hours out, triggers targeted reminders via Twilio + SendGrid, and auto-offers freed slots to your waitlist.",
    stats: [
      { label: "NO-SHOW RATE", value: "6.8%", delta: "-54%" },
      { label: "WAITLIST FILL RATE", value: "71%", delta: "+48%" },
      { label: "WEEKLY REVENUE PROTECTED", value: "$18.4k", delta: "median" },
    ],
    triggers: "appointment.created",
    roles: ["Admin", "MA"],
  },
  {
    slug: "refills",
    index: "M-04",
    name: "Rx Refill Concierge",
    tagline: "Auto-approves safe refills. Escalates only what matters.",
    description:
      "Cross-checks medication, dosage, allergies, last visit, and controlled-substance rules. Auto-approves green-lit refills and queues everything else for physician review with a one-tap decision.",
    stats: [
      { label: "AUTO-APPROVAL RATE", value: "74%", delta: "safe refills" },
      { label: "PHYSICIAN MINUTES / DAY", value: "-42m", delta: "per provider" },
      { label: "INTERACTION ALERTS", value: "100%", delta: "screened" },
    ],
    triggers: "refill.requested",
    roles: ["Physician", "Admin", "MA"],
  },
  {
    slug: "care-gaps",
    index: "M-05",
    name: "Care Gap Closer",
    tagline: "Close HEDIS and Stars gaps across your panel, nightly.",
    description:
      "Refreshes the patient panel every night, identifies open measures (A1C, mammogram, colonoscopy, BP control), and sends provider-branded outreach. Tracks closure end-to-end.",
    stats: [
      { label: "GAPS CLOSED / MO", value: "1,240", delta: "median panel" },
      { label: "HEDIS LIFT", value: "+12pts", delta: "composite score" },
      { label: "STARS TREND", value: "4.2 → 4.6", delta: "12mo" },
    ],
    triggers: "patient.panel.updated",
    roles: ["Care Coordinator", "Admin"],
  },
  {
    slug: "denials",
    index: "M-06",
    name: "Denial Rebuttal AI",
    tagline: "Turns every denial into a structured, evidence-backed appeal.",
    description:
      "Parses the remittance advice, pulls clinical evidence from the chart, writes a payer-specific appeal with citations, and files it before the deadline.",
    stats: [
      { label: "APPEAL WIN RATE", value: "68%", delta: "+29%" },
      { label: "DAYS TO APPEAL", value: "1.4d", delta: "-76%" },
      { label: "REVENUE RECOVERED / YR", value: "$312k", delta: "median" },
    ],
    triggers: "claim.denied",
    roles: ["Billing", "Admin"],
  },
];

export const PRICING = [
  {
    slug: "practice",
    name: "Practice",
    price: "$20,000",
    cadence: "/mo",
    blurb: "For independent and small group practices (up to 8 providers).",
    features: [
      "Up to 3 AI modules",
      "8 providers included",
      "10 staff accounts auto-provisioned on payment",
      "FHIR R4 / HL7 integration",
      "Standard support (business hours)",
      "Standard SLA: 99.5%",
      "BAA included",
    ],
    cta: "Start with Practice",
  },
  {
    slug: "system",
    name: "Health System",
    price: "$35,000",
    cadence: "/mo",
    blurb: "For multi-site groups and mid-market health systems.",
    features: [
      "All 6 AI modules",
      "Up to 40 providers",
      "20 staff accounts auto-provisioned on payment",
      "Epic / Athena / Cerner connectors",
      "Priority support (24/5)",
      "Enterprise SLA: 99.9%",
      "Dedicated success manager",
      "Custom prompts & protocols",
    ],
    featured: true,
    cta: "Start with Health System",
  },
  {
    slug: "enterprise",
    name: "Enterprise",
    price: "Custom",
    cadence: "",
    blurb: "For IDNs, ACOs, and payvider networks with > 40 providers.",
    features: [
      "All 6 AI modules + roadmap access",
      "Unlimited providers",
      "Unlimited staff accounts",
      "White-label and co-brand options",
      "24/7 on-call support",
      "99.99% SLA with credits",
      "Private AWS tenancy available",
      "Custom module development",
    ],
    cta: "Talk to sales",
  },
];

export const INTEGRATIONS = [
  "EPIC", "ATHENA", "CERNER", "NEXTGEN", "ECLINICALWORKS", "ELATION",
  "ALLSCRIPTS", "PRACTICE FUSION", "FHIR R4", "HL7", "CCDA", "SUREscripts",
  "AVAILITY", "CHANGE HEALTHCARE", "WAYSTAR", "CLAIM.MD",
];

export const COMPLIANCE_BADGES = [
  { label: "HIPAA", sub: "Compliant" },
  { label: "SOC 2 Type II", sub: "Q3 2026 — see Trust Center" },
  { label: "BAA", sub: "Available with every plan" },
  { label: "AWS", sub: "HIPAA-Eligible Services" },
  { label: "PHI", sub: "AES-256 at rest · TLS 1.3 in transit" },
  { label: "Access", sub: "MFA-enforced · RBAC · 7-yr audit log" },
];

export const CASE_STUDIES = [
  {
    org: "Ridgeline Primary Care",
    region: "Denver, CO · 12 providers",
    quote:
      "We reclaimed roughly 340 staff hours a month in the first quarter — and recovered $84k in denied claims we would have written off.",
    attribution: "Dr. Lena Ortiz, Medical Director",
    metrics: [
      ["Prior auths automated", "94%"],
      ["Denials recovered", "$84k Q1"],
      ["Go-live to first AI action", "6 days"],
    ],
  },
  {
    org: "Harbor & Main Health",
    region: "Portland, ME · 38 providers",
    quote:
      "Scribe alone gave every physician two hours of their evening back. Retention improved within one quarter.",
    attribution: "Mark Delaney, COO",
    metrics: [
      ["Note drafting time", "-72%"],
      ["Provider NPS", "42 → 71"],
      ["EHR go-home time", "6:40pm → 4:55pm"],
    ],
  },
  {
    org: "Sequoia Care Network",
    region: "Sacramento, CA · 140 providers",
    quote:
      "Care Gap Closer moved our Stars composite from 4.2 to 4.6 in twelve months. That's material revenue.",
    attribution: "Priya Banerjee, VP Population Health",
    metrics: [
      ["Stars lift", "4.2 → 4.6"],
      ["Gaps closed / year", "38,400"],
      ["HEDIS composite", "+12 pts"],
    ],
  },
];

export const FAQS = [
  {
    q: "Is CareSenta HIPAA compliant?",
    a: "Yes. CareSenta runs on HIPAA-eligible AWS services (RDS, ECS Fargate, S3, CloudTrail), all PHI is encrypted at rest (AES-256) and in transit (TLS 1.3), every sub-processor is under a BAA, and a BAA is included with every subscription.",
  },
  {
    q: "How long does it take to go live?",
    a: "Most practices are live in 5–10 business days. The EHR connection wizard handles FHIR R4 endpoints, OAuth, and webhook wiring. Your first AI action fires within 60 seconds of module activation.",
  },
  {
    q: "Which EHRs do you integrate with?",
    a: "Epic, Athena, Cerner, NextGen, eClinicalWorks, Elation, Allscripts, and Practice Fusion natively — plus any EHR that exposes FHIR R4 or HL7.",
  },
  {
    q: "Who sees PHI and where does it live?",
    a: "PHI never leaves your AWS tenancy's HIPAA-eligible services. Only the minimum necessary fields are passed to the AI engine, and no PHI is stored in Stripe, logs, or the browser.",
  },
  {
    q: "Can we pilot a single module?",
    a: "Yes. The Practice plan includes up to three modules — most pilots start with Prior Auth Autopilot and Clinical Note Scribe.",
  },
];

export const SAMPLE_TRANSCRIPT = `Dr: Good morning, Mrs. Chen. How have you been since our last visit?

Patient: Not great, honestly. The headaches are back. Mostly in the mornings, and the one yesterday made me nauseous.

Dr: How often are we talking — daily, a few times a week?

Patient: Maybe four or five days a week for the last month. Ibuprofen helps a little, but not like before.

Dr: Any changes to vision, weakness, or trouble speaking?

Patient: No, nothing like that.

Dr: Okay. Your blood pressure today is 148 over 92, pulse 78. Last visit you were 134 over 84. You're also up about 6 pounds.

Patient: I know. I stopped walking when the headaches started.

Dr: Understood. I'd like to adjust your lisinopril from 10 to 20 milligrams and add a low-dose HCTZ. I'll also order a basic metabolic panel and a CBC. If the headaches don't improve in two weeks, we'll get an MRI.

Patient: That sounds good.

Dr: I'll have my team set up a follow-up in three weeks. Please start tracking your morning BP at home.`;
